import { z } from 'zod'

const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ſƀ-ɏ' -]{2,}$/
const ukTelephoneRegex = /^(\+44\s?7\d{3}|07\d{3})\s?\d{3}\s?\d{3}$/
const invalidEnquiryCharsRegex = /[<>`{}]/

const regExpStr =
  '^(?!.*\\.\\.)(?!.*\\.$)(?!^\\.)' + // Prevents double dots and dot at the start or end
  '(' + // Local part
  "([\\w!#$%&'*+/=?^`{|}~-]+" + // One or more characters that are allowed in the local part
  '|"([^"]|\\\\.)*")' + // Or quoted string
  '(?:\\.[\\w!#$%&\'*+/=?^`{|}~-]+|"([^"]|\\\\.)*")*' + // Allow dots in the local part as long as they are not consecutive or at the start/end
  ')' +
  '@' + // At symbol
  '(?![0-9]+$)' + // Assert that the domain part is not numeric only
  '([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)' + // Domain part: 1-63 characters, no leading or trailing hyphen
  '(\\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*' + // Optional subdomains with the same rules
  '\\.[a-zA-Z]{2,}$' // Top-level domain with at least two alphabetic characters

const emailRegex: RegExp = new RegExp(regExpStr)

export const nameBaseSchema = z
  .string()
  .trim()
  .min(2, 'Name must be at least 2 characters')
  .max(80, 'Name is too long')
  .regex(nameRegex, 'Please enter a valid name')

export const telephoneBaseSchema = z
  .string()
  .trim()
  .max(25, 'Telephone Number is too long')
  .regex(ukTelephoneRegex, 'Please enter a valid telephone number')

export const emailBaseSchema = z
  .string()
  .trim()
  .regex(emailRegex, 'Please enter a valid email')
  .max(254, 'Email is too long')

export const enquiryBaseSchema = z
  .string()
  .trim()
  .min(1, 'Enquiry is required')
  .max(2000, 'Enquiry is too long')
  .refine((value) => !invalidEnquiryCharsRegex.test(value), 'Enquiry contains invalid characters')

export const optionalStringBaseSchema = z.string().trim().min(2).optional().default('')

export const contactRequestSchema = z
  .object({
    name: nameBaseSchema,
    telephoneNumber: telephoneBaseSchema,
    email: emailBaseSchema,
    enquiry: enquiryBaseSchema
  })
  .strict()

export const callRequestSchema = z
  .object({
    name: nameBaseSchema,
    telephoneNumber: telephoneBaseSchema,
    alreadyApplied: z.enum(['true', 'false'])
  })
  .strict()

type ContactRequestKey = keyof typeof contactRequestSchema.shape

const validateField = (field: ContactRequestKey, value: string): string | undefined => {
  const result = contactRequestSchema.shape[field].safeParse(value)
  return result.success ? undefined : result.error.issues[0].message
}

export const validateName = (value: string) => validateField('name', value)
export const validateTelephoneNumber = (value: string) => validateField('telephoneNumber', value)
export const validateEmail = (value: string) => validateField('email', value)
export const validateEnquiry = (value: string) => validateField('enquiry', value)
